import React, {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { Tag } from '../../utils/models/Objective';
import { useFeatures } from '../../utils/util/features';
import { useCurrentOrganization } from '../../utils/util/hooks';

interface Props {
  tags: Tag[];
  isEditable: boolean;
  isAdding: boolean;
  setIsAdding: (value: boolean) => void;
  onChangeTags: (tags: Tag[]) => void;
}

const TAG_NAME_MAX_LENGTH = 25;
const TAG_NAME_TRUNCATE_LENGTH = Math.round(TAG_NAME_MAX_LENGTH * 0.7);

const Tags: FC<Props> = ({
  tags,
  isEditable,
  isAdding,
  setIsAdding,
  onChangeTags,
}) => {
  const { formatMessage } = useIntl();
  const { isOptionEnabled } = useFeatures();
  const organizationId = useCurrentOrganization().id;
  const tagsEnabled = isOptionEnabled('objectives', 'tags_enabled');
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleRemove = useCallback(
    (index: number) => {
      const newTags = tags.filter((_, i) => i !== index);
      onChangeTags(newTags);
    },
    [tags, onChangeTags]
  );

  const addTag = useCallback(
    (tagName: string) => {
      const trimmed = tagName.trim();
      if (trimmed && !tags.some((t) => t.name === trimmed)) {
        const existing = suggestions.find((s) => s.name === trimmed);
        onChangeTags([...tags, existing ?? { name: trimmed }]);
      }
      setInputValue('');
      setIsAdding(false);
      setShowSuggestions(false);
      setSuggestions([]);
    },
    [tags, onChangeTags, suggestions, setIsAdding]
  );

  const handleAdd = useCallback(() => {
    addTag(inputValue);
  }, [inputValue, addTag]);

  const handleSelectSuggestion = useCallback(
    (suggestion: Tag) => {
      addTag(suggestion.name);
    },
    [addTag]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        return;
      }
      if (['Enter', ',', 'Tab'].includes(e.key)) {
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        } else {
          handleAdd();
        }
        return;
      }
      if (e.key === 'Escape') {
        setInputValue('');
        setIsAdding(false);
        setShowSuggestions(false);
        setSuggestions([]);
      }
    },
    [
      handleAdd,
      handleSelectSuggestion,
      highlightedIndex,
      suggestions,
      setIsAdding,
    ]
  );

  useEffect(() => {
    if (!isAdding || !organizationId || inputValue.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(() => {
      ConfirmAPI.sendRequestToConfirm(
        'GET',
        'tags',
        { organization: organizationId, q: inputValue.trim() },
        (data: Tag[] | null) => {
          if (data) {
            const filtered = data.filter(
              (s) => !tags.some((t) => t.name === s.name)
            );
            setSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
            setHighlightedIndex(-1);
          }
        }
      );
    }, 200);

    return () => clearTimeout(timer);
  }, [inputValue, isAdding, organizationId, tags]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Delay to allow click on suggestion to fire first
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (dropdownRef.current?.contains(relatedTarget)) {
        return;
      }
      setTimeout(() => {
        handleAdd();
      }, 150);
    },
    [handleAdd]
  );

  if (!tagsEnabled) return null;
  if (!tags?.length && (!isEditable || !isAdding)) return null;

  return (
    <div
      className="d-flex flex-wrap align-items-center gap-1"
      style={{ minWidth: 0 }}
    >
      {tags?.map((tag, index) => (
        <span
          key={tag.id ?? index}
          className="badge rounded-pill bg-secondary-soft d-inline-flex align-items-center"
          title={tag.name}
        >
          {tag.name.length > TAG_NAME_TRUNCATE_LENGTH
            ? tag.name.slice(0, TAG_NAME_TRUNCATE_LENGTH) + '…'
            : tag.name}
          {isEditable && (
            <span
              role="button"
              className="ms-1 flex-shrink-0"
              onClick={() => handleRemove(index)}
            >
              {'\u00d7'}
            </span>
          )}
        </span>
      ))}
      {isEditable && isAdding && (
        <div className="position-relative d-inline-block">
          <input
            autoFocus
            className="form-control form-control-sm d-inline-block"
            style={{ width: '120px', height: '24px', fontSize: '0.8rem' }}
            maxLength={TAG_NAME_MAX_LENGTH}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={formatMessage({
              id: 'app.views.objectives.tags.input_placeholder',
              defaultMessage: 'Type tag...',
            })}
          />
          {inputValue.length >= TAG_NAME_MAX_LENGTH * 0.8 && (
            <div
              className={
                'small ' +
                (inputValue.length >= TAG_NAME_MAX_LENGTH
                  ? 'text-warning'
                  : 'text-muted')
              }
              style={{ fontSize: '0.7rem' }}
            >
              {inputValue.length}
              {'/'}
              {TAG_NAME_MAX_LENGTH}
            </div>
          )}
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={dropdownRef}
              className="position-absolute bg-white border rounded shadow-sm"
              style={{
                top: '100%',
                left: 0,
                zIndex: 1050,
                width: '180px',
                maxHeight: '150px',
                overflowY: 'auto',
              }}
            >
              {suggestions.map((suggestion, idx) => (
                <div
                  key={suggestion.id ?? suggestion.name}
                  role="button"
                  tabIndex={-1}
                  className={
                    'px-2 py-1 cursor-pointer small' +
                    (idx === highlightedIndex ? ' bg-primary text-white' : '')
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectSuggestion(suggestion);
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  {suggestion.name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(Tags);
